import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
} from '@angular/core';

import { UrlFieldValidators } from 'src/app/components/url-field/url-field-validators';

import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import {
  YoutubeValidationResult,
  YouTubeValidationService,
} from '../services/youtube-validation-service.service';
import { UserService } from 'src/app/settings/services/user.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';

import { environment } from 'src/environments/environment';

@Component({
  selector: 'template-component-youtube',
  templateUrl: './youtube.component.html',
  styleUrls: ['./youtube.component.scss'],
  standalone: false,
})
export class YouTubeComponent implements OnInit {
  spinner = false;
  componentId: string;
  source: string;
  volume: number;
  validationStatus: YoutubeValidationResult['status'];
  termsAccepted = false;

  constructor(
    private elementRef: ElementRef,
    private componentsFactory: ComponentsService,
    private attributeDataService: AttributeDataService,
    private youTubeValidationService: YouTubeValidationService,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private userStateService: UserStateService,
  ) {
    componentsFactory.registerDirective({
      type: 'rise-youtube',
      element: this.elementRef.nativeElement,
      show: () => {
        this.componentId = this.componentsFactory.selected.id;

        this.load();
      },
    });
  }

  load() {
    this.source = this.attributeDataService.getAvailableAttributeData(
      this.componentId,
      'source',
    );
    this.volume =
      this.attributeDataService.getAvailableAttributeData(
        this.componentId,
        'volume',
      ) || 0;
  }

  ngOnInit(): void {
    this.termsAccepted =
      !!this.userStateService.getCopyOfProfile().settings?.youtubeTermsAccepted;
  }

  acceptTerms() {
    this.spinner = true;
    this.userService
      .updateUser(this.userStateService.getUsername(), {
        settings: {
          youtubeTermsAccepted: true,
        },
      })
      .then(() => {
        this.termsAccepted = true;
      })
      .finally(() => {
        this.spinner = false;
      });
  }

  isValidYouTubeUrl(url: string) {
    /**
     * JavaScript function to match (and return) the video Id
     * of any valid Youtube Url, given as input string.
     * @author: Stephan Schmitz <eyecatchup@gmail.com>
     * @url: https://stackoverflow.com/a/10315969/624466
     */
    const urlWithVideoId =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|live\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
    // Shorts IDs must be exactly 11 characters, followed only by a query or fragment
    const urlWithShortsId =
      /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/((\w|-){11})(?:[?#]\S*)?$/;
    const urlWithPlaylistId =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:playlist\?list=|playlist\?.+&list=))(?:\S+)?$/;
    const urlWithUserHandle =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(@[^?#&]+))(?:\S+)?$/;

    return (
      urlWithVideoId.test(url) ||
      urlWithShortsId.test(url) ||
      urlWithPlaylistId.test(url) ||
      urlWithUserHandle.test(url)
    );
  }

  validateSource() {
    //clear the error
    this.validationStatus = undefined;

    const _source = !this.source ? '' : this.source.trim();
    if (_source === '') {
      //empty string is allowed
      return true;
    }

    if (UrlFieldValidators.isInsecureHttpUrl(_source)) {
      this.source = _source.replace('http://', 'https://');
    }

    if (this.isValidYouTubeUrl(_source)) {
      return true;
    }

    this.validationStatus = 'INVALID';
    return false;
  }

  async saveSource() {
    if (this.validateSource()) {
      this.validationStatus = 'VALID';

      this.saveEnvironment();

      this.attributeDataService.setAttributeData(
        this.componentId,
        'source',
        this.source,
      );

      this.spinner = true;
      this.changeDetectorRef.detectChanges();

      const result = await this.youTubeValidationService.validate(this.source);
      if (result.data?.kind === 'youtube#channel') {
        this.attributeDataService.setAttributeData(
          this.componentId,
          'channelId',
          result.data?.id,
        );
        this.attributeDataService.setAttributeData(
          this.componentId,
          'thumbnailUrl',
          result.data?.snippet?.thumbnails?.high?.url,
        );
      } else {
        this.attributeDataService.setAttributeData(
          this.componentId,
          'channelId',
          null,
        );
        this.attributeDataService.setAttributeData(
          this.componentId,
          'thumbnailUrl',
          null,
        );
      }

      this.validationStatus = result.status;
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  saveVolume() {
    this.saveEnvironment();
    this.attributeDataService.setAttributeData(
      this.componentId,
      'volume',
      this.volume,
    );
  }

  saveEnvironment() {
    this.attributeDataService.setAttributeData(
      this.componentId,
      'environment',
      environment.production ? 'prod' : 'test',
    );
  }
}
